<template>
  <v-row>
    <v-dialog v-model="showDialog" scrollable max-width="860px">
      <v-card style="border-radius: 6px;">
        <v-card-title>
          {{ $t("wkt.admin.vacation-policy.unit-change.title") }}
        </v-card-title>

        <v-card-text>
          <v-alert
            v-if="!showResetStep"
            dense
            outlined
            text
            :icon="showRatioStep ? 'info' : 'warning'"
            :type="showRatioStep ? 'info' : 'warning'"
            style="margin-bottom: 18px;"
          >
            <span style="color: #1f2833; font-size: 14px; line-height: 1.5;">
              {{
                showRatioStep
                  ? $t("wkt.admin.vacation-policy.unit-change.convert_warning")
                  : $t("wkt.admin.vacation-policy.unit-change.description")
              }}
            </span>
          </v-alert>

          <v-row v-if="showRatioStep" style="margin-bottom: 12px;">
            <v-col cols="12" md="5" style="padding-top: 0;">
              <div
                style="color: #4f5966; font-size: 13px; line-height: 1.45; margin-bottom: 26px"
              >
                {{ $t("wkt.admin.vacation-policy.unit-change.convert_prompt") }}
              </div>

              <v-text-field
                v-model="ratioHoursPerDay"
                dense
                type="number"
                min="0.01"
                step="0.01"
                style="margin-top: 10px; max-width: 220px;"
                :label="$t('wkt.admin.vacation-policy.unit-change.ratio')"
                :hint="$t('wkt.admin.vacation-policy.unit-change.ratio_hint')"
                persistent-hint
                :rules="[ratioRule]"
              />
            </v-col>
          </v-row>

          <v-row v-if="showResetStep" style="margin-bottom: 12px;">
            <v-col cols="12" style="padding-top: 0;">
              <v-alert
                outlined
                text
                icon="warning"
                type="error"
                style="margin-bottom: 18px; margin-top: 16px;"
              >
                <span
                  style="color: #1f2833; font-size: 14px; line-height: 1.5;"
                >
                  {{
                    $t("wkt.admin.vacation-policy.unit-change.reset_warning")
                  }}
                </span>
              </v-alert>

              <v-text-field
                v-model="resetConfirmationText"
                dense
                style="margin-top: 10px; max-width: 360px;"
                :label="resetConfirmationLabel"
                :rules="[resetConfirmationRule]"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" md="6">
              <div
                style="padding: 14px; border: 1px solid #e7ebf1; border-radius: 6px; background: #fafbfd;"
              >
                <span
                  style="display: block; margin-bottom: 6px; color: #98a0ac; font-size: 11px; line-height: 1.35;"
                >
                  {{ $t("wkt.admin.vacation-policy.unit-change.source_unit") }}
                </span>
                <span
                  style="color: #27313d; font-size: 14px; line-height: 1.45;"
                >
                  {{ sourceUnitText }}
                </span>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div
                style="padding: 14px; border: 1px solid #e7ebf1; border-radius: 6px; background: #fafbfd;"
              >
                <span
                  style="display: block; margin-bottom: 6px; color: #98a0ac; font-size: 11px; line-height: 1.35;"
                >
                  {{ $t("wkt.admin.vacation-policy.unit-change.target_unit") }}
                </span>
                <span
                  style="color: #27313d; font-size: 14px; line-height: 1.45;"
                >
                  {{ targetUnitText }}
                </span>
              </div>
            </v-col>
          </v-row>
        </v-card-text>

        <v-divider />

        <v-card-actions style="padding: 12px 18px;">
          <!-- Initial state: choose Reset / Convert / Cancel -->
          <template v-if="!showRatioStep && !showResetStep">
            <v-btn
              color="warning"
              text
              style="font-size: 0.9em; font-weight: 400;"
              @click="showResetStep = true"
            >
              {{
                $t("wkt.admin.vacation-policy.unit-change.reset").toUpperCase()
              }}
            </v-btn>
            <v-spacer />
            <v-btn
              color="#185EA9"
              text
              style="font-size: 0.9em; font-weight: 400;"
              @click="showRatioStep = true"
            >
              {{
                $t(
                  "wkt.admin.vacation-policy.unit-change.convert",
                ).toUpperCase()
              }}
            </v-btn>
            <v-btn
              text
              style="font-size: 0.9em; font-weight: 400;"
              @click="showDialog = false"
            >
              {{ $t("wkt.admin.common.dialog-buttons.cancel").toUpperCase() }}
            </v-btn>
          </template>
          <!-- Ratio (convert) step -->
          <template v-else-if="showRatioStep">
            <v-btn
              text
              style="font-size: 0.9em; font-weight: 400;"
              @click="showRatioStep = false"
            >
              {{
                $t("wkt.admin.vacation-policy.unit-change.back").toUpperCase()
              }}
            </v-btn>
            <v-spacer />
            <v-btn
              color="#185EA9"
              text
              style="font-size: 0.9em; font-weight: 400;"
              @click="confirmConvert"
            >
              {{
                $t(
                  "wkt.admin.vacation-policy.unit-change.convert",
                ).toUpperCase()
              }}
            </v-btn>
            <v-btn
              text
              style="font-size: 0.9em; font-weight: 400;"
              @click="showDialog = false"
            >
              {{ $t("wkt.admin.common.dialog-buttons.cancel").toUpperCase() }}
            </v-btn>
          </template>
          <!-- Reset confirmation step -->
          <template v-else-if="showResetStep">
            <v-btn
              text
              style="font-size: 0.9em; font-weight: 400;"
              @click="
                showResetStep = false;
                resetConfirmationText = '';
              "
            >
              {{
                $t("wkt.admin.vacation-policy.unit-change.back").toUpperCase()
              }}
            </v-btn>
            <v-spacer />
            <v-btn
              color="warning"
              text
              style="font-size: 0.9em; font-weight: 400;"
              @click="confirmReset"
            >
              {{
                $t("wkt.admin.vacation-policy.unit-change.reset").toUpperCase()
              }}
            </v-btn>
            <v-btn
              text
              style="font-size: 0.9em; font-weight: 400;"
              @click="showDialog = false"
            >
              {{ $t("wkt.admin.common.dialog-buttons.cancel").toUpperCase() }}
            </v-btn>
          </template>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import BaseModule from "../../common/BaseModule";

const HOURS_BAG_UNIT_DAYS = 0;
const HOURS_BAG_UNIT_HOURS = 1;

export default {
  name: "VacationPolicyUnitChangeDialog",
  mixins: [BaseModule],
  data() {
    return {
      showDialog: false,
      showRatioStep: false,
      showResetStep: false,
      options: null,
      callback: null,
      ratioHoursPerDay: 8,
      resetConfirmationText: "",
    };
  },
  computed: {
    sourceUnitText() {
      return this.formatUnit(
        this.options ? this.options.sourceUnit : HOURS_BAG_UNIT_DAYS,
      );
    },
    targetUnitText() {
      return this.formatUnit(
        this.options ? this.options.targetUnit : HOURS_BAG_UNIT_DAYS,
      );
    },
    resetConfirmationToken() {
      return this.$t("wkt.admin.vacation-policy.unit-change.reset_token");
    },
    resetConfirmationLabel() {
      return this.$t(
        "wkt.admin.vacation-policy.unit-change.reset_type_prompt",
        {
          token: this.resetConfirmationToken,
        },
      );
    },
  },
  methods: {
    open(options, callback) {
      this.options = options || {};
      this.callback = callback;
      this.showRatioStep = false;
      this.showResetStep = false;
      this.resetConfirmationText = "";
      this.ratioHoursPerDay = Number(
        this.options && this.options.defaultRatioHoursPerDay
          ? this.options.defaultRatioHoursPerDay
          : 8,
      );
      this.showDialog = true;
    },
    formatUnit(unit) {
      return Number(unit) === HOURS_BAG_UNIT_HOURS
        ? this.$t("wkt.admin.vacation-policy.unit.hours")
        : this.$t("wkt.admin.vacation-policy.unit.days");
    },
    ratioRule(value) {
      return (
        Number(value) > 0 ||
        this.$t("wkt.admin.vacation-policy.unit-change.ratio_error")
      );
    },
    resetConfirmationRule(value) {
      return (
        (value || "").trim().toUpperCase() ===
          this.resetConfirmationToken.toUpperCase() ||
        this.$t(
          "wkt.admin.vacation-policy.unit-change.reset_confirmation_error",
          {
            token: this.resetConfirmationToken,
          },
        )
      );
    },
    confirmReset() {
      if (this.resetConfirmationRule(this.resetConfirmationText) !== true) {
        return;
      }

      this.showDialog = false;

      if (this.callback) {
        this.callback({
          action: "reset",
          ratioHoursPerDay: null,
        });
      }
    },
    confirmConvert() {
      if (this.ratioRule(this.ratioHoursPerDay) !== true) {
        return;
      }

      this.showDialog = false;

      if (this.callback) {
        this.callback({
          action: "convert",
          ratioHoursPerDay: Number(this.ratioHoursPerDay),
        });
      }
    },
  },
};
</script>
